/**
 * Canned knowledge base for the floating chat assistant.
 *
 * Answers must stay factually aligned with faqContent.js and the real funnel routes. Prices are
 * never written here — they are fetched from the server so they cannot drift from plans.js.
 * Nothing here calls an LLM, so anything not written below is answered by the fallback path.
 */

export const WHATSAPP_NUMBER = '+918252113477';

/** Action ids resolved by ChatWidget into navigation / scroll / external links. */
export const ACTIONS = {
  START: 'start_assessment',
  DEMO: 'try_demo',
  COUNSELLORS: 'counsellors',
  WHATSAPP: 'whatsapp',
  PRIVACY: 'privacy',
  SCROLL_LEAD: 'scroll:get-started',
  SCROLL_HOW: 'scroll:how-it-works',
  SCROLL_FAQ: 'scroll:faq',
  SCROLL_SCHOOLS: 'scroll:schools',
  SCROLL_PRICING: 'scroll:get-started',
};

const ACTION_LABELS = {
  [ACTIONS.START]: 'Start my assessment',
  [ACTIONS.DEMO]: 'Try a free demo',
  [ACTIONS.COUNSELLORS]: 'Meet the counsellors',
  [ACTIONS.WHATSAPP]: 'Chat on WhatsApp',
  [ACTIONS.PRIVACY]: 'Read privacy policy',
  [ACTIONS.SCROLL_LEAD]: 'Go to the form',
  [ACTIONS.SCROLL_HOW]: 'See how it works',
  [ACTIONS.SCROLL_FAQ]: 'Open full FAQs',
  [ACTIONS.SCROLL_SCHOOLS]: 'School partnership info',
};

export function actionLabel(id) {
  return ACTION_LABELS[id] || 'Learn more';
}

/**
 * Prices are never hardcoded here — they come from the server's plans.js via /api/chat/config, so a
 * pricing change cannot leave the assistant quoting a stale figure.
 *
 * @param {{ name: string, pricePaise: number, bullets: string[] }[]} plans
 */
export function buildPricingAnswer(plans) {
  if (!Array.isArray(plans) || plans.length === 0) {
    return 'There are three plans. The entry plan covers the assessment and your personalised report, and the higher plans add group counselling and then 1:1 sessions for you and your parents.\n\nThe current prices are listed in the pricing section on this page. You can also try a free demo first.';
  }
  const lines = plans.map(
    (p) => `• **${p.name} — ₹${Math.round(Number(p.pricePaise || 0) / 100)}**: ${(p.bullets || []).join('; ')}.`,
  );
  return `Here are the plans:\n\n${lines.join('\n')}\n\nYou can also try a free demo first if you want to see the style of the questions before paying.`;
}

export const GREETING = {
  student: {
    text: "Hi! I'm the ChooseRightCareer assistant. Ask me anything about Career DNA, pricing, or how the assessment works — I answer instantly.",
    chips: ['What is Career DNA?', 'How much does it cost?', "I'm in Class 10", 'How long does it take?'],
  },
  school: {
    text: "Hi! I'm the ChooseRightCareer assistant. Ask me about school pilots, rollout, cohort sizes, or co-branded reports — I answer instantly.",
    chips: ['How do school pilots work?', 'Which classes do you cover?', 'Can we co-brand reports?', 'Book a demo'],
  },
};

/**
 * Intents are scored by keyword overlap. `phrases` are high-signal multi-word matches
 * (worth more than a single keyword) and `keywords` are single tokens.
 */
export const INTENTS = [
  {
    id: 'what_is_career_dna',
    audience: 'both',
    phrases: ['career dna', 'what is this', 'what do you do', 'how is it different', 'what is career'],
    keywords: ['dna', 'framework', 'different', 'unique', 'about', 'explain', 'meaning'],
    answer:
      'Career DNA is a structured 7-trait framework. Instead of only looking at your marks or a list of interests, it looks at how you think, work, and decide.\n\nThose traits are combined with AI and expert logic, so the report reads like it was written about you — not a generic personality label.',
    chips: ['How long does it take?', 'How does the AI help?', 'What do I get at the end?'],
    actions: [ACTIONS.SCROLL_HOW, ACTIONS.DEMO],
  },
  {
    id: 'pricing',
    audience: 'both',
    phrases: ['how much', 'what is the price', 'is it free', 'cost of', 'payment plan', 'plan price'],
    keywords: ['price', 'pricing', 'cost', 'fees', 'fee', 'charge', 'rupees', 'money', 'paid', 'plans', 'plan', 'expensive', 'afford', 'free'],
    dynamic: 'pricing',
    answer: '',
    chips: ['Try the free demo', 'What is in the report?', 'Do I have to pay for counselling?'],
    actions: [ACTIONS.DEMO, ACTIONS.START],
  },
  {
    id: 'duration',
    audience: 'both',
    phrases: ['how long', 'how much time', 'how many minutes', 'how many questions', 'time does it take', 'takes how'],
    keywords: ['long', 'duration', 'minutes', 'time', 'quick', 'fast', 'questions'],
    answer:
      'Most students finish in about 8–10 minutes. It is one sitting, no preparation needed, and there are no right or wrong answers.\n\nAs soon as you finish you get a clear, easy-to-read PDF report you can go through with your parents or a counsellor.',
    chips: ['What is in the report?', 'How much does it cost?'],
    actions: [ACTIONS.START],
  },
  {
    id: 'classes',
    audience: 'both',
    phrases: ['which class', 'what class', 'class 9', 'class 10', 'class 11', 'class 12', 'which grade', 'am i too young', 'too late'],
    keywords: ['class', 'classes', 'grade', 'std', 'standard', '9th', '10th', '11th', '12th', 'age'],
    answer:
      'The programme is built for Class 9 to 12 — the years when stream and career decisions actually start to matter.\n\nIf you are in Class 9 or 10, it mostly helps you pick a stream with reasons instead of pressure. In Class 11 or 12, it focuses more on narrowing down courses and entrance paths. The ideas still apply whenever you are choosing a next step.',
    chips: ['What is Career DNA?', 'How much does it cost?', 'Start my assessment'],
    actions: [ACTIONS.START],
  },
  {
    id: 'stream_limited',
    audience: 'student',
    phrases: ['only for science', 'only science', 'only commerce', 'arts student', 'humanities', 'not good at maths', 'weak in maths'],
    keywords: ['science', 'commerce', 'arts', 'humanities', 'stream', 'pcm', 'pcb', 'maths'],
    answer:
      'No — this is not limited to science or commerce students.\n\nThe framework covers traditional and modern paths: design, tech, business, creative fields, and more. That is deliberate, so your options are not squeezed into the same three or four "popular" careers everyone hears about.',
    chips: ['What is Career DNA?', 'What careers can it suggest?'],
    actions: [ACTIONS.DEMO],
  },
  {
    id: 'report_contents',
    audience: 'both',
    phrases: ['what is in the report', 'what do i get', 'what will i get', 'sample report', 'see a report', 'report look like', 'what careers'],
    keywords: ['report', 'pdf', 'result', 'results', 'output', 'sample', 'careers', 'suggest'],
    answer:
      'You get a personalised PDF built around your Career DNA traits. It covers your working style, your strengths and struggles, a blind spot worth knowing about, three career directions matched to your actual answers, an action plan, and a short section written for your parents.\n\nIt is yours to keep and share.',
    chips: ['How long does it take?', 'Can parents see my results?', 'How much does it cost?'],
    actions: [ACTIONS.DEMO, ACTIONS.START],
  },
  {
    id: 'ai_role',
    audience: 'both',
    phrases: ['how does ai', 'how does the ai', 'ai help', 'is it ai', 'ai generated', 'chatgpt', 'is this automated', 'real people'],
    keywords: ['ai', 'artificial', 'automated', 'algorithm', 'robot', 'human', 'expert', 'experts'],
    answer:
      'AI maps your answers onto patterns in the Career DNA model and writes the insights in plain language.\n\nIt does not replace human judgement — experts shape the framework itself, and the optional counselling sessions add the human depth on top.',
    chips: ['Who are the counsellors?', 'What if I disagree with my report?'],
    actions: [ACTIONS.COUNSELLORS],
  },
  {
    id: 'privacy',
    audience: 'both',
    phrases: ['is my data safe', 'data private', 'privacy policy', 'who sees my', 'personal information', 'delete my data'],
    keywords: ['privacy', 'private', 'data', 'safe', 'secure', 'confidential', 'gdpr', 'information'],
    answer:
      'Your details are used to generate your report and to follow up only in the ways you allow.\n\nThe Privacy Policy has the full detail on what is collected, how it is used, and the choices you have.',
    chips: ['Can parents see my results?'],
    actions: [ACTIONS.PRIVACY],
  },
  {
    id: 'parents',
    audience: 'student',
    phrases: ['can parents see', 'my parents', 'show my parents', 'parents pressure', 'convince my parents', 'for parents'],
    keywords: ['parent', 'parents', 'mother', 'father', 'family', 'mom', 'dad'],
    answer:
      'The PDF is yours to share. Plenty of students go through it with their parents or a teacher, and the report includes a short section written specifically for parents — which often makes that conversation a lot easier.\n\nThe Pro Decision Plan also includes a dedicated 1:1 session for parents.',
    chips: ['How much does it cost?', 'What is in the report?'],
    actions: [ACTIONS.START],
  },
  {
    id: 'counselling',
    audience: 'both',
    phrases: ['talk to a counsellor', 'speak to someone', 'one on one', 'do i have to pay for counselling', 'who are the counsellors', 'human help'],
    keywords: ['counsellor', 'counselor', 'counselling', 'counseling', 'mentor', 'session', 'guidance', 'advisor'],
    answer:
      'The assessment and report work fine on their own. Counselling is optional and layered on top: the Career Blueprint plan adds a group session and a live expert Q&A, and the Pro Decision Plan adds 1:1 sessions for you and for your parents.\n\nAsk me about pricing if you want the current plan costs.',
    chips: ['How much does it cost?', 'How does the AI help?'],
    actions: [ACTIONS.COUNSELLORS, ACTIONS.SCROLL_LEAD],
  },
  {
    id: 'disagree',
    audience: 'student',
    phrases: ['what if i disagree', 'not accurate', 'is it correct', 'what if it is wrong', 'dont agree', 'trust the result'],
    keywords: ['disagree', 'wrong', 'accurate', 'accuracy', 'correct', 'trust', 'reliable', 'valid'],
    answer:
      'The report is a structured reflection, not a verdict on your life.\n\nUse it to open up options, talk it through with a counsellor, and combine it with your own research and interests. If something in it feels off, that reaction is useful information too.',
    chips: ['Who are the counsellors?', 'How does the AI help?'],
    actions: [ACTIONS.COUNSELLORS],
  },
  {
    id: 'how_to_start',
    audience: 'student',
    phrases: ['how do i start', 'how to begin', 'get started', 'sign up', 'i want to start', 'take the test', 'where do i start', 'start my assessment', 'start the assessment'],
    keywords: ['start', 'begin', 'signup', 'register', 'join', 'enroll', 'apply'],
    answer:
      'Two ways in:\n\n1. **Free demo** — a short taste of the questions, no payment and no commitment.\n2. **Full assessment** — enter your details, pick a plan, and go straight into the questions. About 8–10 minutes, and your report is ready at the end.',
    chips: ['How much does it cost?', 'How long does it take?'],
    actions: [ACTIONS.DEMO, ACTIONS.START],
  },
  {
    id: 'demo',
    audience: 'student',
    phrases: ['free demo', 'try before', 'trial', 'test it first', 'try it out', 'free version'],
    keywords: ['demo', 'free', 'trial', 'sample', 'preview', 'try'],
    answer:
      'Yes — there is a free demo. It gives you a feel for the style of the questions and the kind of insight you get, before you decide about the full report.',
    chips: ['How much does it cost?', 'Start my assessment'],
    actions: [ACTIONS.DEMO],
  },
  {
    id: 'payment_issue',
    audience: 'both',
    phrases: ['payment failed', 'money deducted', 'not received report', 'refund', 'payment issue', 'did not get my report', 'stuck on payment'],
    keywords: ['refund', 'failed', 'deducted', 'debited', 'stuck', 'error', 'problem', 'issue', 'support'],
    answer:
      'Sorry about that — payment and report-delivery issues are handled by a person, not by me.\n\nMessage the team on WhatsApp with your registered email or phone number and they will trace the payment and get your report to you.',
    chips: [],
    actions: [ACTIONS.WHATSAPP],
    escalate: true,
  },
  {
    id: 'school_pilot',
    audience: 'school',
    phrases: ['school pilot', 'school partnership', 'bulk assessment', 'for our school', 'book a demo', 'how do we get started', 'partner with you'],
    keywords: ['school', 'pilot', 'partnership', 'bulk', 'cohort', 'principal', 'institution', 'rollout', 'demo'],
    answer:
      'A typical school engagement includes onboarding, student access to the assessment, PDF reports for every student, and optional workshops or expert-led sessions — shaped around your calendar.\n\nThe fastest way in is to book a demo. The team will walk you through rollout options, timelines, and the pricing band that fits your context.',
    chips: ['Which classes do you cover?', 'Can we co-brand reports?'],
    actions: [ACTIONS.SCROLL_SCHOOLS, ACTIONS.WHATSAPP],
  },
  {
    id: 'school_branding',
    audience: 'school',
    phrases: ['white label', 'co brand', 'our logo', 'branded report', 'custom branding'],
    keywords: ['whitelabel', 'brand', 'branding', 'logo', 'cobrand', 'customise', 'customize'],
    answer:
      'Co-branding and programme packaging depend on the partnership tier. Worth raising during your demo so the team can show the options that fit your brand guidelines.',
    chips: ['How do school pilots work?'],
    actions: [ACTIONS.SCROLL_SCHOOLS],
  },
  {
    id: 'school_data',
    audience: 'school',
    phrases: ['student data', 'data handling', 'consent', 'dpa', 'compliance', 'retention'],
    keywords: ['consent', 'compliance', 'dpa', 'retention', 'policy', 'legal'],
    answer:
      'We align with each institution on consent, access, and retention. The Privacy Policy describes how information is processed, and DPA-style terms can be discussed for formal partnerships.',
    chips: ['How do school pilots work?'],
    actions: [ACTIONS.PRIVACY, ACTIONS.SCROLL_SCHOOLS],
  },
  {
    id: 'school_support',
    audience: 'school',
    phrases: ['teacher training', 'support for counsellors', 'how are assessments scheduled', 'schedule bulk'],
    keywords: ['training', 'orientation', 'teachers', 'staff', 'schedule', 'scheduling', 'window'],
    answer:
      'Staff get orientation materials, optional walkthroughs, and clear escalation paths so reports are interpreted consistently.\n\nFor scheduling, we define assessment windows together — career week, term-wise, or grade by grade — along with roster handling and parent communication templates.',
    chips: ['How do school pilots work?'],
    actions: [ACTIONS.SCROLL_SCHOOLS],
  },
  {
    id: 'contact',
    audience: 'both',
    phrases: ['talk to a human', 'contact you', 'phone number', 'customer care', 'real person', 'email you'],
    keywords: ['contact', 'human', 'call', 'phone', 'email', 'reach', 'whatsapp', 'helpline'],
    answer:
      'Happy to hand you over. WhatsApp is the quickest route to a real person on the team, and you can also leave your details in the form on this page and they will reach out.',
    chips: [],
    actions: [ACTIONS.WHATSAPP, ACTIONS.SCROLL_LEAD],
    escalate: true,
  },
  {
    id: 'greeting',
    audience: 'both',
    phrases: ['hi there', 'hey there', 'good morning', 'good evening', 'good afternoon'],
    keywords: ['hi', 'hello', 'hey', 'namaste', 'yo', 'hola'],
    answer: 'Hey! Good to see you. What would you like to know?',
    chips: ['What is Career DNA?', 'How much does it cost?', 'How long does it take?'],
    actions: [],
  },
  {
    id: 'thanks',
    audience: 'both',
    phrases: ['thank you', 'thanks a lot', 'that helps', 'got it'],
    keywords: ['thanks', 'thank', 'thx', 'appreciate', 'helpful'],
    answer: 'Anytime. If anything else comes up, just ask — and good luck with the decision.',
    chips: ['Start my assessment', 'Try the free demo'],
    actions: [],
  },
];

export const FALLBACK = {
  text: "I don't have a confident answer for that one — I only work from a fixed set of answers, so I'd rather not guess.\n\nHere's what I can definitely help with:",
  chips: ['What is Career DNA?', 'How much does it cost?', 'How long does it take?', 'Which classes is it for?'],
};

export const ESCALATION = {
  text: 'That one is better answered by a person on the team. They usually reply on WhatsApp within a few hours.',
  actions: [ACTIONS.WHATSAPP, ACTIONS.SCROLL_LEAD],
};
